import { useState } from 'react';
import './SettingsMenu.css';

interface Props {
  /** Absent si le produit courant n'accepte pas de design (tour de cou). */
  onImportDesign?: () => void;
  onResetModel: () => void;
  onPersonnaliser: () => void;
  onAbout: () => void;
  onTutorial?: () => void;
  onClose?: () => void;
}

function MenuItem({
  icon,
  label,
  onClick,
  danger = false,
  chevron = false,
  active = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  chevron?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      className={`smenu-item${danger ? ' smenu-item--danger' : ''}${active ? ' smenu-item--active' : ''}`}
      onClick={onClick}
    >
      <span className="smenu-item__icon">{icon}</span>
      <span className="smenu-item__label">{label}</span>
      <span className={`smenu-item__arrow${active ? ' is-open' : ''}`}>
        {chevron ? (
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M7 8l3 3 3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M8 5l4 5-4 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </span>
    </button>
  );
}

export default function SettingsMenu({
  onImportDesign,
  onResetModel,
  onPersonnaliser,
  onAbout,
  onTutorial,
  onClose,
}: Props) {
  const [devisOpen, setDevisOpen] = useState(false);

  return (
    <div className="smenu">
      {onClose && (
        <div className="smenu__mobile-header">
          <span className="smenu__mobile-title">Menu</span>
          <button type="button" className="smenu__close-btn" onClick={onClose} aria-label="Fermer">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── SECTION 1 : Création ── */}
      <div className="smenu__section-label">Création</div>

      <MenuItem
        icon={
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M6 3h8l2 4-6 2-6-2 2-4Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
            <path d="M4 7l-2 2 2 1v7h12v-7l2-1-2-2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
        label="Personnaliser le textile"
        onClick={onPersonnaliser}
      />

      {onImportDesign && (
        <MenuItem
          icon={
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M10 13V4m0 0-3 3m3-3 3 3M4 14v.5A2.5 2.5 0 0 0 6.5 17h7a2.5 2.5 0 0 0 2.5-2.5V14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          label="Importer un design"
          onClick={onImportDesign}
        />
      )}

      <MenuItem
        icon={
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M4 8V4H8M4 8A7 7 0 1 1 5.06 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
        label="Réinitialiser le modèle"
        onClick={onResetModel}
        danger
      />

      <div className="smenu__separator" />

      {/* ── SECTION 2 : Informations ── */}
      <div className="smenu__section-label">Informations</div>

      <MenuItem
        icon={
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.7"/>
            <path d="M7 9h6M7 12h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
          </svg>
        }
        label="Demander un devis"
        onClick={() => setDevisOpen(o => !o)}
        chevron
        active={devisOpen}
      />

      {devisOpen && (
        <div className="smenu__contact-panel">
          <p className="smenu__contact-text">
            Terminez simplement votre configuration, puis transmettez-la à notre équipe via le bouton <strong>« Transmettre à Indyanna Pub »</strong> en bas de page. Nous vous enverrons un devis personnalisé dans les plus brefs délais.
          </p>
        </div>
      )}

      <MenuItem
        icon={
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M4 4h12a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7l-4 3V5a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
        label="Contact"
        onClick={() => window.open('https://www.indyannapub.com/contact/', '_blank', 'noopener,noreferrer')}
      />

      <MenuItem
        icon={
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.7"/>
            <path d="M10 9v5M10 7v.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"/>
          </svg>
        }
        label="À propos d'Indyanna Studio"
        onClick={onAbout}
      />

      <MenuItem
        icon={
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M11 4h5v5M16 4l-7 7M8 5H5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
        label="Accéder au site Indyanna Pub"
        onClick={() => window.open('https://www.indyannapub.com/', '_blank', 'noopener,noreferrer')}
      />

      {onTutorial && (
        <>
          <div className="smenu__separator" />
          <MenuItem
            icon={
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.7"/>
                <path d="M8 8c0-1.1.9-2 2-2s2 .9 2 2c0 1-.6 1.5-1.3 2-.5.3-.7.7-.7 1v.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                <circle cx="10" cy="14.5" r=".7" fill="currentColor"/>
              </svg>
            }
            label="Reprendre le tutoriel"
            onClick={onTutorial}
          />
        </>
      )}
    </div>
  );
}
